import type { LiveAttack, LiveSheetAbility } from "../../types/generated/live";
import { interpolate, type Labels } from "./labels";

export function signed(value: number): string {
	if (value > 0) return `+${value}`;
	return value === 0 ? "+0" : String(value);
}

export function formatWeight(grams: number, locale: string): string {
	if (grams <= 0) return "-";
	const number = (n: number) => n.toLocaleString(locale, { maximumFractionDigits: 3 });
	return grams >= 1000 ? `${number(grams / 1000)} kg` : `${number(grams)} g`;
}

// A class ability's choice: free text as stored, then a mastery's weapon groups worded by `abilities.group_*`.
export function choiceText({ choice, choice_groups }: Pick<LiveSheetAbility, "choice" | "choice_groups">, labels: Labels): string {
	return [choice, ...choice_groups.map((group) => labels.abilities[`group_${group}`] ?? group)].filter(Boolean).join(", ");
}

// Today's sheet leaves the melee bonus off a shooting weapon and off ammunition.
export function showsBac({ kind }: Pick<LiveAttack, "kind">): boolean {
	return kind !== "shoot" && kind !== "ammo";
}

// An item's stored description; a racial weapon's is worded here, in the viewer's language, from its size and damage type.
export function propertiesText(
	{ kind, properties, weapon_size, damage_type }: Pick<LiveAttack, "kind" | "properties" | "weapon_size" | "damage_type">,
	attacks: Labels["attacks"],
): string | undefined {
	if (kind !== "natural") return properties ?? undefined;
	return [weapon_size && attacks[`size_${weapon_size}`], damage_type && attacks[damage_type]].filter(Boolean).join(", ") || undefined;
}

// The chance to knock out: 1 up to the range on 1d6, worded as one value when the range is 1.
export function knockoutText(range: number, { knockout_range, knockout_single }: Labels["attacks"]): string {
	return range > 1 ? interpolate(knockout_range, { range }) : knockout_single;
}
